import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site-shell";
import { GuideCard } from "@/components/guide-card";
import { GenerateCta } from "@/components/generate-cta";
import { listPublishedGuides } from "@/lib/guides.functions";

export const Route = createFileRoute("/guides/")({
  loader: () => listPublishedGuides(),
  head: () => {
    const title = "Freelance Contract Guides — DealDraft";
    const description =
      "Plain-language explainers for kill fees, usage rights, revision limits, and other freelance contract clauses — written so you can copy the language into your next proposal.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "DealDraft" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: "https://dealdraft.pro/guides" }],
    };
  },
  component: GuidesIndexPage,
});

function GuidesIndexPage() {
  const guides = Route.useLoaderData();

  return (
    <SiteShell>
      <section className="mx-auto max-w-3xl px-6 pt-16 pb-12 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          Freelance Contract Guides
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg">
          Kill fees, usage rights, revision caps, and payment terms are where freelance deals
          usually go sideways. These guides explain the clauses in plain language and show how to
          write them into a proposal and contract — then generate both in DealDraft.
        </p>
        <div className="mt-8">
          <GenerateCta size="lg" className="h-12 rounded-lg px-7 text-base">
            Generate Your First Proposal Free
          </GenerateCta>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-20">
        <div data-guides-grid className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {guides.length === 0 ? (
            <p className="col-span-full rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
              New guides are in the works. The index is live so published posts appear here
              automatically — nothing ships until it is finished.
            </p>
          ) : (
            guides.map((guide) => <GuideCard key={guide.slug} guide={guide} />)
          )}
        </div>
      </section>
    </SiteShell>
  );
}
